import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

// Reusable inner-page hero. Optional background image + breadcrumb.
export const PageHero = ({ eyebrow, title, intro, image, breadcrumb = [], align = "left", children, accent = "teal", compact = false }) => {
  const reduce = useReducedMotion();
  const center = align === "center";
  return (
    <section className={cn("relative overflow-hidden hero-noise", compact ? "pt-32 pb-14" : "pt-36 pb-20")} data-testid="page-hero">
      {image && (
        <>
          <div className="absolute inset-0">
            <img src={image} alt="" className="h-full w-full object-cover opacity-30" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--bp-bg)] via-[color:var(--bp-bg)]/80 to-[color:var(--bp-bg)]/55" />
        </>
      )}
      <div className="container-bp relative">
        {breadcrumb.length > 0 && (
          <nav className="mb-6 flex items-center gap-1.5 text-[12px] text-[color:var(--bp-text-muted)]">
            <Link to="/" className="hover:text-[color:var(--bp-teal)]">Home</Link>
            {breadcrumb.map((b, i) => (
              <span key={i} className="inline-flex items-center gap-1.5">
                <ChevronRight className="h-3 w-3" />
                {b.to ? <Link to={b.to} className="hover:text-[color:var(--bp-teal)]">{b.label}</Link> : <span className="text-[color:var(--bp-cream)]">{b.label}</span>}
              </span>
            ))}
          </nav>
        )}
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 24 }}
          animate={reduce ? {} : { opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className={cn("max-w-3xl", center && "mx-auto text-center")}
        >
          {eyebrow && (
            <p className={cn("eyebrow inline-flex items-center gap-2", accent === "gold" ? "text-[color:var(--bp-gold)]" : "text-[color:var(--bp-teal)]")}>
              <span className={cn("h-px w-8", accent === "gold" ? "bg-[color:var(--bp-gold)]" : "bg-[color:var(--bp-teal)]")} /> {eyebrow}
            </p>
          )}
          <h1 className="mt-6 font-display text-4xl font-semibold leading-[1.08] tracking-[-0.02em] text-[color:var(--bp-cream)] sm:text-5xl lg:text-[3.5rem]">
            {title}
          </h1>
          {intro && <p className="mt-6 max-w-2xl text-[15px] leading-[1.75] text-[color:var(--bp-text-muted)] sm:text-base">{intro}</p>}
          {children && <div className={cn("mt-9", center && "flex justify-center")}>{children}</div>}
        </motion.div>
      </div>
    </section>
  );
};
